import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import { api } from "../../../singleton/axios";
import { MySubscriptions } from "../types";
import { MY_SUBSCRIPTIONS_QUERY_KEY } from "./useMySubscriptions";

interface UnsubscribeVariables {
  id: string;
}

/**
 * Turn one alert off. Optimistic against the shared subscriptions list, so the
 * button on the ticker page and the row on Manage alerts change together.
 */
export const useUnsubscribe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: UnsubscribeVariables) => {
      try {
        await api.delete(`/notification-subscriptions/${encodeURIComponent(id)}`);
      } catch (error) {
        // Already gone (turned off in another tab): that is the state asked for.
        if (isAxiosError(error) && error.response?.status === 404) return;
        throw error;
      }
    },

    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: MY_SUBSCRIPTIONS_QUERY_KEY });
      const previous = queryClient.getQueryData<MySubscriptions>(MY_SUBSCRIPTIONS_QUERY_KEY);
      queryClient.setQueryData<MySubscriptions>(MY_SUBSCRIPTIONS_QUERY_KEY, (old) => {
        if (!old) return old;
        return {
          ...old,
          subscriptions: old.subscriptions.filter((subscription) => subscription.id !== id),
        };
      });
      return { previous };
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(MY_SUBSCRIPTIONS_QUERY_KEY, context.previous);
      }
      enqueueSnackbar("Couldn't turn off that alert. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_SUBSCRIPTIONS_QUERY_KEY });
    },
  });
};
